import React, { useState, useEffect, useContext } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CombinedContext } from "../../context/combinedContext";
import companyBasedRequest from "../../request/companyBasedRequest";
import BannerMessageFixed from "../common/bannerMessageFixed/bannerMessageFixed";
import logo from "../../images/LogoFullText.png";
import "./deleteShopConfirm.css";

const DeleteShopConfirm = () => {
	const [searchParams] = useSearchParams();
	const shopId = searchParams.get("shopId");
	const token = searchParams.get("token");

	const { language, setUserData } = useContext(CombinedContext);

	const [verifying, setVerifying] = useState(true);
	const [shopName, setShopName] = useState("");
	const [staffCount, setStaffCount] = useState(0);
	const [blockingError, setBlockingError] = useState(false);
	const [banner, setBanner] = useState(null);

	const [confirming, setConfirming] = useState(false);
	const [deleted, setDeleted] = useState(false);

	const showPersistentBanner = (type, text) => {
		setBanner({ type, text });
		if (type === "error") setBlockingError(true);
	};

	useEffect(() => {
		if (!shopId || !token) {
			showPersistentBanner("error", language === "es" ? "Enlace de eliminación inválido." : "Invalid deletion link.");
			setVerifying(false);
			return;
		}

		const verify = async () => {
			try {
				const res = await companyBasedRequest.verifyShopDeletion(shopId, token);
				setShopName(res.shopName || "");
				setStaffCount(res.staffCount || 0);
			} catch (err) {
				const code = err.response?.data?.error;
				const messages = {
					expired_token: {
						es: "Este enlace de confirmación ha expirado. Vuelve a solicitar la eliminación desde tu cuenta.",
						en: "This confirmation link has expired. Please request the deletion again from your account.",
					},
					invalid_token: {
						es: "Este enlace de confirmación no es válido.",
						en: "This confirmation link is invalid.",
					},
				};
				const msg =
					messages[code]?.[language] ??
					(language === "es" ? "No se pudo verificar este enlace. Puede que ya haya sido usado." : "We couldn't verify this link. It may have already been used.");
				showPersistentBanner("error", msg);
			} finally {
				setVerifying(false);
			}
		};

		verify();
	}, [shopId, token, language]);

	const handleConfirm = async () => {
		try {
			setConfirming(true);
			await companyBasedRequest.confirmShopDeletion(shopId, token);
			setDeleted(true);

			/* If this browser happens to be signed in as the (now-deleted) owner, clear the stale session */
			["idToken", "accessToken", "refreshToken", "user", "shop"].forEach((key) => {
				localStorage.removeItem(key);
				sessionStorage.removeItem(key);
			});
			setUserData(null);
		} catch (err) {
			showPersistentBanner(
				"error",
				err.message || (language === "es" ? "No se pudo eliminar la cuenta. Inténtalo de nuevo." : "Could not delete the account. Please try again."),
			);
		} finally {
			setConfirming(false);
		}
	};

	return (
		<div className="sf-dsc-root">
			<div className="sf-dsc-card">
				<img src={logo} alt="Sin Fila" className="sf-dsc-logo" />

				{verifying ? (
					<p className="sf-dsc-sub">{language === "es" ? "Verificando enlace..." : "Verifying link..."}</p>
				) : deleted ? (
					<>
						<h1 className="sf-dsc-title">{language === "es" ? "Cuenta eliminada" : "Account deleted"}</h1>
						<p className="sf-dsc-sub">
							{language === "es"
								? "Tu negocio y todos sus datos operativos han sido eliminados de Sin Fila. Se cerró la sesión de todo el personal, incluyéndote a ti."
								: "Your shop and all its operational data have been deleted from Sin Fila. Everyone on staff — including you — has been signed out."}
						</p>
						<Link to="/signin" className="sf-dsc-button">
							{language === "es" ? "Ir a Sin Fila" : "Go to Sin Fila"}
						</Link>
					</>
				) : !blockingError ? (
					<>
						<h1 className="sf-dsc-title">
							{language === "es" ? "¿Eliminar " : "Delete "}
							<strong>{shopName}</strong>
							{language === "es" ? "?" : "?"}
						</h1>
						<p className="sf-dsc-sub">
							{language === "es"
								? "Esta acción es permanente. Se eliminará el perfil del negocio, las cuentas de todo el personal, los horarios y las reservas futuras (se notificará a los clientes). Las reseñas e historial dejarán de ser visibles."
								: "This action is permanent. It will delete the shop profile, all staff accounts, schedules, and future bookings (customers will be notified). Reviews and history will no longer be visible."}
						</p>
						{staffCount > 0 && (
							<p className="sf-dsc-sub">
								{language === "es"
									? `Esto afectará a ${staffCount} cuenta(s) de personal (incluyendo al dueño).`
									: `This will affect ${staffCount} staff account(s) (including the owner).`}
							</p>
						)}
						<p className="sf-dsc-sub sf-dsc-sub--warning">
							{language === "es"
								? "Se cerrará la sesión de todo el personal, incluyéndote a ti, de inmediato."
								: "Everyone on staff — including you — will be signed out immediately."}
						</p>
						<button className="sf-dsc-button sf-dsc-button--danger" onClick={handleConfirm} disabled={confirming}>
							{confirming
								? language === "es"
									? "Eliminando..."
									: "Deleting..."
								: language === "es"
								? "Sí, eliminar permanentemente"
								: "Yes, permanently delete"}
						</button>
						<Link to="/" className="sf-dsc-cancel">
							{language === "es" ? "Cancelar" : "Cancel"}
						</Link>
					</>
				) : null}
			</div>

			{banner?.text && <BannerMessageFixed type={banner.type} text={banner.text} />}
		</div>
	);
};

export default DeleteShopConfirm;
